import { z } from 'zod';
/**
 * A purchase-order number as the reviewer types it. Kept exactly as entered
 * (trimmed): Elixir owns the format, and some deployments are case-sensitive.
 */
export const poNumber = z.string().trim().min(1, 'Enter a PO number').max(60);
/** Fetches one PO from the Elixir API by its number, to compare with the invoice. */
export const purchaseOrderLookupRequest = z.object({ poNumber: poNumber });
/**
 * Links one PO to the invoice.
 *
 * Only the PO number crosses the wire: the server fetches the PO from Elixir
 * again and compares against Elixir's figures, so a client can never supply
 * PO figures of its own.
 */
export const linkPurchaseOrderRequest = z.object({ poNumber: poNumber });
// ── The Elixir API response, as it arrives ──────────────────
// Lenient on purpose: nearly every field may be null, and amounts arrive as
// decimal strings. Unknown keys are dropped rather than rejected so a new
// field upstream does not break the lookup.
const nullableString = z.string().nullish();
const decimal = z.union([z.string(), z.number()]).nullish();
export const elixirPurchaseOrderLine = z.object({
    lineNumber: z.number().nullish(),
    itemCode: nullableString,
    description: nullableString,
    hsnSac: nullableString,
    uom: nullableString,
    quantityOrdered: decimal,
    quantityReceived: decimal,
    quantityInvoiced: decimal,
    unitPrice: decimal,
    discountAmount: decimal,
    taxRatePercent: decimal,
    taxAmount: decimal,
    lineSubtotal: decimal,
    lineTotal: decimal,
    grnNumbers: z.array(z.string()).nullish(),
});
export const elixirPurchaseOrder = z.object({
    poNumber: z.string().min(1),
    poRevision: z.union([z.string(), z.number()]).nullish(),
    poDate: nullableString,
    status: nullableString,
    currency: nullableString,
    buyer: z
        .object({
        companyCode: nullableString,
        companyName: nullableString,
        gstin: nullableString,
        country: nullableString,
        departmentCode: nullableString,
        locationCode: nullableString,
        costCentre: nullableString,
    })
        .nullish(),
    vendor: z
        .object({
        vendorCode: nullableString,
        name: nullableString,
        gstin: nullableString,
        pan: nullableString,
        taxId: nullableString,
        country: nullableString,
    })
        .nullish(),
    terms: z
        .object({
        paymentTermsDays: z.number().nullish(),
        incoterms: nullableString,
        deliveryDate: nullableString,
    })
        .nullish(),
    amounts: z
        .object({
        subtotal: decimal,
        taxAmount: decimal,
        totalAmount: decimal,
        invoicedToDate: decimal,
        remainingAmount: decimal,
    })
        .nullish(),
    tolerance: z
        .object({
        priceVariancePercent: decimal,
        quantityVariancePercent: decimal,
        amountVarianceAbsolute: decimal,
    })
        .nullish(),
    lineItems: z.array(elixirPurchaseOrderLine).nullish(),
    updatedAt: nullableString,
});
/** `POST /purchaseord` with `{ poNumber }` answers with one PO, or none. */
export const elixirPurchaseOrderResponse = z.object({
    success: z.boolean(),
    purchaseOrder: elixirPurchaseOrder.nullish(),
});
//# sourceMappingURL=purchaseOrder.js.map